import React from "react";
import { compress as compressJSON, decompress as decompressJSON } from "compress-json";
import { useRouter } from "next/router";
import { deflate as compressString, inflate as decompressString } from "pako";
import { APPLICATION_BASE_PATH } from "./next/config";

interface URLState {
  source: string | undefined;
  expression: string | undefined;
}

type UsePlaygroundURLStateSetter = (currentState: URLState) => URLState;

interface UsePlaygroundURLStateProps {
  defaultSource: string | undefined;
  defaultExpression: string | undefined;
}

export const usePlaygroundURLState = ({ defaultExpression, defaultSource }: UsePlaygroundURLStateProps) => {
  const router = useRouter();
  const URLState = getStateBasedOnURL({
    currentStateParameterValue: getCurrentURLStateQueryParameterValue(router.query),
    v1StateParameterValue: getV1URLStateQueryParameterValue(router.query),
  });

  const defaultURLState = React.useMemo(
    () => ({
      source: defaultSource,
      expression: defaultExpression,
    }),
    [defaultExpression, defaultSource],
  );

  const setURLState = React.useCallback(
    async (stateSetter: UsePlaygroundURLStateSetter, options: { replace: boolean } = { replace: false }) => {
      if (!router.isReady) {
        return;
      }

      const currentURLState = URLState ?? {
        source: undefined,
        expression: undefined,
      };

      const newURLState = stateSetter(currentURLState);

      const newEncodedURLState = encodeCurrentURLState(newURLState);
      /**
       * Ensure that the "legacy" way of handling state is not preserved.
       */
      if (router.query[V1_STATE_URL_PARAMETER_NAME]) {
        delete router.query[V1_STATE_URL_PARAMETER_NAME];
      }
      /**
       * The `router` call can fail on Firefox if the URL is too long (2 MiB size limit).
       * See https://github.com/Stedi/mappings/issues/415#issuecomment-1097654211 for more failure modes.
       */
      const method = options.replace ? "replace" : "push";
      return await router[method]({ query: { ...router.query, statev2: newEncodedURLState } });
    },
    [URLState, router],
  );

  React.useEffect(() => {
    if (URLState !== undefined) {
      return;
    }

    /**
     * For simplicity sake we assume that the defaults are safe since we control them.
     */
    void setURLState(() => defaultURLState, { replace: true });
  }, [URLState, defaultURLState, setURLState]);

  return [URLState ?? defaultURLState, setURLState] as const;
};

interface GetStateBasedOnURL {
  v1StateParameterValue: string | undefined;
  currentStateParameterValue: string | undefined;
}
const getStateBasedOnURL = ({
  currentStateParameterValue,
  v1StateParameterValue,
}: GetStateBasedOnURL): URLState | undefined => {
  if (currentStateParameterValue) {
    try {
      return decodeCurrentURLState(currentStateParameterValue);
    } catch (_e) {
      return undefined;
    }
  }

  if (v1StateParameterValue) {
    try {
      return decodeV1URLState(v1StateParameterValue);
    } catch (_e) {
      return undefined;
    }
  }

  return undefined;
};

export const encodeCurrentURLState = (currentURLState: URLState) => {
  const compressedState = compressJSON({
    source: currentURLState.source ?? "",
    expression: currentURLState.expression ?? "",
  });
  const zippedState = compressString(JSON.stringify(compressedState));
  const encodedURLState = Buffer.from(zippedState).toString("base64");

  return encodedURLState;
};

export const encodeV1URLState = (v1URLState: URLState) => {
  return Buffer.from(JSON.stringify(v1URLState)).toString("base64");
};

const decodeCurrentURLState = (encodedCurrentURLState: string) => {
  const rawDecodedURLState = Buffer.from(encodedCurrentURLState, "base64");
  const unzippedState = decompressString(rawDecodedURLState, { to: "string" });
  const decompressedState = decompressJSON(JSON.parse(unzippedState));

  return decompressedState;
};

const decodeV1URLState = (encodedV1URLState: string) => {
  return JSON.parse(Buffer.from(encodedV1URLState, "base64").toString("utf-8"));
};

export const getPlaygroundLinkForV1State = (v1URLStateQueryParameterValue: string | undefined) => {
  const { origin } = window.location;
  /**
   * In theory this should never happen.
   */
  if (!v1URLStateQueryParameterValue) {
    return `${origin}${APPLICATION_BASE_PATH}`;
  }

  /**
   * We only want the `state` search param. User might have set other parameters (like the height of the panels) which we do not want to carry over.
   */
  return `${origin}${APPLICATION_BASE_PATH}?${V1_STATE_URL_PARAMETER_NAME}=${encodeURIComponent(
    v1URLStateQueryParameterValue,
  )}`;
};

export const getPlaygroundLinkForCurrentState = (currentStateQueryParameterValue: string | undefined) => {
  const { origin } = window.location;
  /**
   * In theory this should never happen.
   */
  if (!currentStateQueryParameterValue) {
    return `${origin}${APPLICATION_BASE_PATH}`;
  }

  /**
   * We only want the `state` search param. User might have set other parameters (like the height of the panels) which we do not want to carry over.
   */
  return `${origin}${APPLICATION_BASE_PATH}?${CURRENT_STATE_URL_PARAMETER_NAME}=${encodeURIComponent(
    currentStateQueryParameterValue,
  )}`;
};

export const getCurrentURLStateQueryParameterValue = (
  queryParameters: Record<string, string[] | string | undefined>,
) => {
  return normalizeStateParameterValue(queryParameters[CURRENT_STATE_URL_PARAMETER_NAME]);
};

export const getV1URLStateQueryParameterValue = (queryParameters: Record<string, string[] | string | undefined>) => {
  return normalizeStateParameterValue(queryParameters[V1_STATE_URL_PARAMETER_NAME]);
};

const normalizeStateParameterValue = (stateParameterValue: string[] | string | undefined) => {
  if (stateParameterValue === undefined) {
    return undefined;
  }
  return Array.isArray(stateParameterValue) ? stateParameterValue[0] : stateParameterValue;
};

/**
 * Compressed using pako & compress-json. Base64 encoded.
 */
export const CURRENT_STATE_URL_PARAMETER_NAME = "statev2";

/**
 * Naively base64 encoded without any compression.
 */
const V1_STATE_URL_PARAMETER_NAME = "state";
